import {
    API_BASE_URL,
    resolveGalleryCmsMediaUrl,
    resolveGalleryMediaUrl,
} from '../config/api';
import { parseNewsListMeta } from './cmsNews';

const GALLERY_LIST_FETCH_PAGE_SIZE = 50;
const MAX_PAGES = 30;

const FAIL_ALL_GALLERY_LIST = 'Galeriler alınamadı';

/**
 * @param {Response} res
 * @param {string} failLabel
 * @returns {Promise<{ list: unknown[], metaData: unknown }>}
 */
async function parseListResponse(res, failLabel) {
    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error(`${failLabel} (yanıt okunamadı)`);
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `${failLabel} (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : failLabel;
        throw new Error(msg);
    }

    const raw = /** @type {{ data?: unknown, metaData?: unknown }} */ (obj)
        .data;
    const list = Array.isArray(raw) ? raw : [];
    const metaData =
        /** @type {{ metaData?: unknown }} */ (obj).metaData ?? null;

    return { list, metaData };
}

/**
 * Tekil `data` nesnesi (detay uçları).
 * @param {Response} res
 * @param {string} failLabel
 * @returns {Promise<Record<string, unknown>>}
 */
async function parseObjectResponse(res, failLabel) {
    const text = await res.text();
    /** @type {unknown} */
    let body;
    try {
        body = text ? JSON.parse(text) : {};
    } catch {
        throw new Error(`${failLabel} (yanıt okunamadı)`);
    }

    if (!res.ok) {
        const msg =
            body &&
            typeof body === 'object' &&
            'message' in body &&
            typeof /** @type {{ message?: string }} */ (body).message ===
                'string'
                ? /** @type {{ message: string }} */ (body).message
                : null;
        throw new Error(msg || `${failLabel} (${res.status})`);
    }

    const obj = body && typeof body === 'object' ? body : {};
    if (/** @type {{ success?: boolean }} */ (obj).success === false) {
        const msg =
            typeof /** @type {{ message?: string | null }} */ (obj).message ===
            'string'
                ? /** @type {{ message: string }} */ (obj).message
                : failLabel;
        throw new Error(msg);
    }

    const data = /** @type {{ data?: unknown }} */ (obj).data;
    if (data == null || typeof data !== 'object') {
        throw new Error(failLabel);
    }
    return /** @type {Record<string, unknown>} */ (data);
}

/**
 * `GET /galleries/api/AllGallery/GetAll` satırı.
 * @param {unknown} row
 * @returns {import('../data/galleries').GalleryAlbum | null}
 */
export function mapAllGalleryRowToAlbum(row) {
    if (!row || typeof row !== 'object') return null;
    const o = /** @type {Record<string, unknown>} */ (row);
    const rawId = o.id != null ? String(o.id) : '';
    if (!rawId) return null;

    const title = typeof o.title === 'string' ? o.title : '';
    const imageCount = Number(o.imageCount) || 0;
    const coverRaw =
        typeof o.coverImageUrl === 'string' ? o.coverImageUrl.trim() : '';
    const coverImageSrc = coverRaw
        ? resolveGalleryMediaUrl(coverRaw)
        : '/assets/images/gallery-cover-placeholder.svg';

    const createdAt =
        typeof o.createdAt === 'string' && o.createdAt.trim()
            ? o.createdAt.trim()
            : undefined;

    const related = mapGalleryRelatedEntity(o);

    return {
        id: rawId,
        slug: rawId,
        title,
        photoCount: imageCount,
        coverImageSrc,
        ...(createdAt ? { createdAt } : {}),
        ...related,
    };
}

/**
 * @param {Record<string, unknown>} o
 */
function mapGalleryRelatedEntity(o) {
    const type = Number(o.relatedEntityType);
    const rid =
        o.relatedEntityId != null && String(o.relatedEntityId).trim()
            ? String(o.relatedEntityId).trim()
            : '';
    if (!rid) return {};
    if (type === 1) return { eventId: rid };
    if (type === 2) return { meetingId: rid };
    return {};
}

/**
 * @param {'club' | 'meeting'} source
 * @param {unknown} row
 * @returns {import('../data/galleries').GalleryAlbum | null}
 */
export function mapApiGalleryRowToAlbum(row, source) {
    if (!row || typeof row !== 'object') return null;
    const o = /** @type {Record<string, unknown>} */ (row);
    const rawId = o.id != null ? String(o.id) : '';
    if (!rawId) return null;

    const id = `${source}-${rawId}`;
    const title = typeof o.title === 'string' ? o.title : '';
    const imageCount = Number(o.imageCount) || 0;
    const coverRaw =
        typeof o.coverImageUrl === 'string' ? o.coverImageUrl.trim() : '';
    const coverImageSrc = coverRaw
        ? resolveGalleryCmsMediaUrl(coverRaw)
        : '/assets/images/gallery-cover-placeholder.svg';

    const createdAt =
        typeof o.createdAt === 'string' && o.createdAt.trim()
            ? o.createdAt.trim()
            : undefined;

    const eventId =
        source === 'club' && typeof o.eventId === 'string' && o.eventId.trim()
            ? o.eventId.trim()
            : undefined;
    const meetingId =
        source === 'meeting' &&
        typeof o.meetingId === 'string' &&
        o.meetingId.trim()
            ? o.meetingId.trim()
            : undefined;

    return {
        id,
        slug: id,
        title,
        photoCount: imageCount,
        coverImageSrc,
        ...(createdAt ? { createdAt } : {}),
        ...(eventId ? { eventId } : {}),
        ...(meetingId ? { meetingId } : {}),
    };
}

/**
 * @typedef {{
 *   title: string,
 *   description: string | null,
 *   images: Array<{ id: string, src: string, thumb: string, alt: string, order: number }>,
 * }} GalleryDetailPayload
 */

/**
 * @param {Record<string, unknown>} data
 * @param {(path: string) => string} [resolveMediaUrl]
 * @returns {GalleryDetailPayload}
 */
export function mapGalleryDetailPayload(
    data,
    resolveMediaUrl = resolveGalleryCmsMediaUrl,
) {
    const title = typeof data.title === 'string' ? data.title : '';
    const description =
        data.description != null && typeof data.description === 'string'
            ? data.description
            : null;
    const rawImages = Array.isArray(data.images) ? data.images : [];
    /** @type {GalleryDetailPayload['images']} */
    const images = [];

    rawImages.forEach((im, idx) => {
        if (!im || typeof im !== 'object') return;
        const img = /** @type {Record<string, unknown>} */ (im);
        const id = img.id != null ? String(img.id) : `img-${idx}`;
        const path =
            typeof img.url === 'string' && img.url.trim()
                ? img.url.trim()
                : '';
        if (!path) return;
        const src = resolveMediaUrl(path);
        const thumbRaw =
            typeof img.thumbnailUrl === 'string' && img.thumbnailUrl.trim()
                ? img.thumbnailUrl.trim()
                : path;
        const thumb = resolveMediaUrl(thumbRaw);
        const alt = typeof img.altText === 'string' ? img.altText : '';
        const order = Number(img.order) || 0;
        images.push({ id, src, thumb: thumb || src, alt, order });
    });

    images.sort((a, b) => a.order - b.order);

    return { title, description, images };
}

/**
 * `AllGallery/GetById` yanıtı — `/api/galleries/` medya öneki.
 * @param {Record<string, unknown>} data
 * @returns {GalleryDetailPayload}
 */
export function mapAllGalleryDetailPayload(data) {
    return mapGalleryDetailPayload(data, resolveGalleryMediaUrl);
}

/**
 * Detay yanıtından liste kartı özeti.
 * @param {string} galleryId
 * @param {GalleryDetailPayload} payload
 * @returns {import('../data/galleries').GalleryAlbum}
 */
export function galleryAlbumFromDetailPayload(galleryId, payload) {
    const first = payload.images[0];
    return {
        id: galleryId,
        slug: galleryId,
        title: payload.title,
        photoCount: payload.images.length,
        coverImageSrc:
            first?.thumb ||
            first?.src ||
            '/assets/images/gallery-cover-placeholder.svg',
    };
}

const FAIL_ALL_GALLERY_DETAIL = 'Galeri yüklenemedi';
const FAIL_DETAIL_CLUB = 'Galeri yüklenemedi';
const FAIL_DETAIL_MEETING = 'Galeri yüklenemedi';

/**
 * `GET /clubevents/ClubEvent/GetGallery?eventId=`
 * @param {string} accessToken
 * @param {string} eventId
 * @returns {Promise<GalleryDetailPayload>}
 */
export async function fetchClubGalleryDetail(accessToken, eventId) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    if (!eventId) {
        throw new Error(FAIL_DETAIL_CLUB);
    }
    const params = new URLSearchParams({
        eventId,
    });
    const url = `${API_BASE_URL}/clubevents/ClubEvent/GetGallery?${params}`;
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
    });
    const data = await parseObjectResponse(res, FAIL_DETAIL_CLUB);
    return mapGalleryDetailPayload(data);
}

/**
 * `GET /meetings/api/Meeting/{meetingId}/Gallery`
 * @param {string} accessToken
 * @param {string} meetingId
 * @returns {Promise<GalleryDetailPayload>}
 */
export async function fetchMeetingGalleryDetail(accessToken, meetingId) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    if (!meetingId) {
        throw new Error(FAIL_DETAIL_MEETING);
    }
    const url = `${API_BASE_URL}/meetings/api/Meeting/${encodeURIComponent(meetingId)}/Gallery`;
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
    });
    const data = await parseObjectResponse(res, FAIL_DETAIL_MEETING);
    return mapGalleryDetailPayload(data);
}

/**
 * `GET /galleries/api/AllGallery/GetAll` — sayfalı liste (Bearer).
 * @param {string} accessToken
 * @param {{ pageNumber?: number, pageSize?: number }} [opts]
 * @returns {Promise<{
 *   items: import('../data/galleries').GalleryAlbum[],
 *   meta: ReturnType<typeof parseNewsListMeta>,
 * }>}
 */
export async function fetchAllGalleryListPage(
    accessToken,
    { pageNumber = 1, pageSize = 10 } = {},
) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }

    const page = Math.max(1, pageNumber);
    const size = Math.max(1, Math.min(100, pageSize));
    const params = new URLSearchParams({
        pageNumber: String(page),
        pageSize: String(size),
    });
    const url = `${API_BASE_URL}/galleries/api/AllGallery/GetAll?${params}`;
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
    });

    const { list, metaData } = await parseListResponse(
        res,
        FAIL_ALL_GALLERY_LIST,
    );

    /** @type {import('../data/galleries').GalleryAlbum[]} */
    const items = [];
    for (const row of list) {
        const album = mapAllGalleryRowToAlbum(row);
        if (album) items.push(album);
    }

    const meta = parseNewsListMeta(metaData, items.length, page, size);
    return { items, meta };
}

/**
 * `GET /galleries/api/AllGallery/GetById/{id}` (Bearer).
 * @param {string} accessToken
 * @param {string} galleryId
 * @returns {Promise<GalleryDetailPayload>}
 */
export async function fetchAllGalleryById(accessToken, galleryId) {
    if (!accessToken) {
        throw new Error('Oturum gerekli');
    }
    const id = String(galleryId || '').trim();
    if (!id) {
        throw new Error(FAIL_ALL_GALLERY_DETAIL);
    }
    const url = `${API_BASE_URL}/galleries/api/AllGallery/GetById/${encodeURIComponent(id)}`;
    const res = await fetch(url, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
        },
    });
    const data = await parseObjectResponse(res, FAIL_ALL_GALLERY_DETAIL);
    return mapAllGalleryDetailPayload(data);
}

/**
 * URL slug’ına göre galeri detayı (AllGallery veya eski club/meeting önekleri).
 * @param {string} accessToken
 * @param {string} gallerySlug
 * @returns {Promise<{ payload: GalleryDetailPayload, album: import('../data/galleries').GalleryAlbum }>}
 */
export async function fetchGalleryDetailBySlug(accessToken, gallerySlug) {
    const decoded = decodeURIComponent(String(gallerySlug || '').trim());
    if (!decoded) {
        throw new Error(FAIL_ALL_GALLERY_DETAIL);
    }

    if (decoded.startsWith('club-')) {
        const eventId = decoded.slice('club-'.length);
        const payload = await fetchClubGalleryDetail(accessToken, eventId);
        return {
            payload,
            album: {
                id: decoded,
                slug: decoded,
                title: payload.title,
                photoCount: payload.images.length,
                coverImageSrc:
                    payload.images[0]?.thumb ||
                    payload.images[0]?.src ||
                    '/assets/images/gallery-cover-placeholder.svg',
                eventId,
            },
        };
    }

    if (decoded.startsWith('meeting-')) {
        const meetingId = decoded.slice('meeting-'.length);
        const payload = await fetchMeetingGalleryDetail(
            accessToken,
            meetingId,
        );
        return {
            payload,
            album: {
                id: decoded,
                slug: decoded,
                title: payload.title,
                photoCount: payload.images.length,
                coverImageSrc:
                    payload.images[0]?.thumb ||
                    payload.images[0]?.src ||
                    '/assets/images/gallery-cover-placeholder.svg',
                meetingId,
            },
        };
    }

    const payload = await fetchAllGalleryById(accessToken, decoded);
    return {
        payload,
        album: galleryAlbumFromDetailPayload(decoded, payload),
    };
}

/**
 * @param {import('../data/galleries').GalleryAlbum} a
 * @param {import('../data/galleries').GalleryAlbum} b
 * @returns {number}
 */
function compareGalleryByCreatedAtDesc(a, b) {
    const ta = a.createdAt ? Date.parse(a.createdAt) : NaN;
    const tb = b.createdAt ? Date.parse(b.createdAt) : NaN;
    const ma = Number.isFinite(ta) ? ta : 0;
    const mb = Number.isFinite(tb) ? tb : 0;
    if (mb !== ma) return mb - ma;
    return String(a.title).localeCompare(String(b.title), 'tr', {
        sensitivity: 'base',
    });
}

/**
 * Tüm galeri sayfalarını birleştirir (detay sayfası araması vb.).
 * @param {string} accessToken
 * @returns {Promise<import('../data/galleries').GalleryAlbum[]>}
 */
export async function fetchAllGalleriesMerged(accessToken) {
    /** @type {import('../data/galleries').GalleryAlbum[]} */
    const out = [];

    for (let page = 1; page <= MAX_PAGES; page += 1) {
        const { items, meta } = await fetchAllGalleryListPage(accessToken, {
            pageNumber: page,
            pageSize: GALLERY_LIST_FETCH_PAGE_SIZE,
        });
        out.push(...items);
        if (!meta.hasNext || items.length === 0) break;
    }

    out.sort(compareGalleryByCreatedAtDesc);
    return out;
}

/**
 * Slug / id ile albüm arar; bulunana kadar sayfalar (en fazla `MAX_PAGES`).
 * @param {string} accessToken
 * @param {string | undefined} gallerySlug
 * @returns {Promise<import('../data/galleries').GalleryAlbum | null>}
 */
export async function findGalleryAlbumBySlug(accessToken, gallerySlug) {
    if (!gallerySlug || !accessToken) return null;
    const decoded = decodeURIComponent(gallerySlug);

    for (let page = 1; page <= MAX_PAGES; page += 1) {
        const { items, meta } = await fetchAllGalleryListPage(accessToken, {
            pageNumber: page,
            pageSize: GALLERY_LIST_FETCH_PAGE_SIZE,
        });
        const found = findGalleryInMergedList(items, decoded);
        if (found) return found;
        if (!meta.hasNext || items.length === 0) break;
    }

    return null;
}

/**
 * @param {string} s
 */
function looksLikeUuid(s) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
        String(s).trim(),
    );
}

/**
 * Birleşik galeri listesinde URL parametresine göre albüm bulur.
 * Eski yalnızca UUID içeren bağlantılar için `club-` / `meeting-` öneki dener.
 *
 * @param {import('../data/galleries').GalleryAlbum[]} list
 * @param {string | undefined} gallerySlug
 * @returns {import('../data/galleries').GalleryAlbum | null}
 */
export function findGalleryInMergedList(list, gallerySlug) {
    if (!gallerySlug) return null;
    const decoded = decodeURIComponent(gallerySlug);
    const direct = list.find(
        (a) => a.id === decoded || a.slug === decoded,
    );
    if (direct) return direct;
    if (looksLikeUuid(decoded)) {
        const prefixed = list.find(
            (a) =>
                a.id === `club-${decoded}` || a.id === `meeting-${decoded}`,
        );
        if (prefixed) return prefixed;
    }
    return null;
}
